import { AdasBox, AdasTypography } from '@components/wrapper-components'
import { AlertTriangle, Check, CheckCircle2, ChevronDown, Clock, Info, X } from 'lucide-react'
import PropTypes from 'prop-types'
import { useState } from 'react'

// Billing-tab UI primitives (design from feat/3717-billing-tab), built on the Adas wrappers.

// Stripe wordmark, in Stripe's brand indigo (#635BFF), for the "managed by Stripe" attribution.
export const StripeWordmark = ({ className = '' }) => (
  <span className={`font-bold italic lowercase tracking-tight text-[#635BFF] ${className}`}>stripe</span>
)
StripeWordmark.propTypes = { className: PropTypes.string }

// Rounded white card with a soft shadow, used across every billing screen.
export const Card = ({ children, className = '' }) => (
  <AdasBox className={`rounded-xl border border-[#D1D5D8] bg-white shadow-sm ${className}`}>{children}</AdasBox>
)
Card.propTypes = { children: PropTypes.node, className: PropTypes.string }

// Small lucide icon in a soft primary-blue tile, leading a section title.
export const SectionIcon = ({ icon: Icon, className = '' }) => (
  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#F1F5FB] text-[#1F6FEB] ${className}`}>
    <Icon className='h-3.5 w-3.5' aria-hidden='true' />
  </span>
)
SectionIcon.propTypes = { icon: PropTypes.elementType.isRequired, className: PropTypes.string }

// Section title with its optional icon tile.
const SectionTitle = ({ title, icon }) => (
  <span className='flex min-w-0 items-center gap-2.5'>
    {icon && <SectionIcon icon={icon} />}
    <AdasTypography className='text-[15px] font-semibold text-foreground'>{title}</AdasTypography>
  </span>
)
SectionTitle.propTypes = { title: PropTypes.string.isRequired, icon: PropTypes.elementType }

// Card header strip: icon + title on the left, optional action on the right, divider below.
export const CardHeader = ({ title, action, icon }) => (
  <AdasBox className='flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4 sm:px-6'>
    <SectionTitle title={title} icon={icon} />
    {action}
  </AdasBox>
)
CardHeader.propTypes = { title: PropTypes.string.isRequired, action: PropTypes.node, icon: PropTypes.elementType }

// Card whose body collapses behind a clickable header (chevron rotates). `action` sits
// left of the chevron. Defaults to collapsed; pass defaultOpen to start expanded.
export const CollapsibleCard = ({ title, action, icon, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Card>
      <button
        type='button'
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`flex w-full items-center justify-between gap-3 rounded-xl px-5 py-4 text-left sm:px-6 transition-colors hover:bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]/20 ${
          open ? 'border-b border-border' : ''
        }`}
      >
        <SectionTitle title={title} icon={icon} />
        <span className='flex items-center gap-3'>
          {action}
          <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && children}
    </Card>
  )
}
CollapsibleCard.propTypes = {
  title: PropTypes.string.isRequired,
  action: PropTypes.node,
  icon: PropTypes.elementType,
  defaultOpen: PropTypes.bool,
  children: PropTypes.node
}

// Blue uppercase eyebrow label.
export const Eyebrow = ({ children, className = '' }) => (
  <AdasTypography className={`text-[11px] font-bold uppercase tracking-wide text-[#2563EB] ${className}`}>{children}</AdasTypography>
)
Eyebrow.propTypes = { children: PropTypes.node, className: PropTypes.string }

// Feature row with a green check (included) or gray x (excluded).
// `compact` matches the plan cards' description text (13px, muted).
export const FeatureItem = ({ included, label, compact = false }) => (
  <AdasBox className={`flex items-center gap-2 ${compact ? 'py-0.5' : 'py-1'}`}>
    {included ? (
      <Check className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} shrink-0 text-[#16A34A]`} strokeWidth={2.5} />
    ) : (
      <X className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} shrink-0 text-[#94A3B8]`} strokeWidth={2.5} />
    )}
    <span
      className={compact ? 'text-[13px] text-muted-foreground' : `text-[14px] ${included ? 'text-foreground' : 'text-muted-foreground'}`}
    >
      {label}
    </span>
  </AdasBox>
)
FeatureItem.propTypes = { included: PropTypes.bool, label: PropTypes.string.isRequired, compact: PropTypes.bool }

// Small rounded pill with an optional leading dot.
export const Pill = ({ label, tone = 'blue', dot = true, className = '' }) => {
  const tones = {
    blue: 'bg-[#EFF4FF] text-[#2563EB]',
    green: 'bg-[#DCFCE7] text-[#16803F]',
    amber: 'bg-[#FEF3C7] text-[#B45309]',
    red: 'bg-[#FDECEC] text-[#B42318]',
    slate: 'bg-slate-100 text-slate-600'
  }
  const dots = { blue: 'bg-[#2563EB]', green: 'bg-[#16803F]', amber: 'bg-[#D97706]', red: 'bg-[#DC2626]', slate: 'bg-slate-400' }
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold ${tones[tone]} ${className}`}>
      {dot && <span className={`h-1.5 w-1.5 rounded-full ${dots[tone]}`} />}
      {label}
    </span>
  )
}
Pill.propTypes = {
  label: PropTypes.node.isRequired,
  tone: PropTypes.oneOf(['blue', 'green', 'amber', 'red', 'slate']),
  dot: PropTypes.bool,
  className: PropTypes.string
}

// Tone-based status banner at the top of the Billing tab for each subscription state.
// Mirrors the app-wide BroadcastBar treatment referenced in the PRD (§6.2).
export const StatusBanner = ({ tone = 'slate', label, title, body, action }) => {
  const tones = {
    amber: {
      wrap: 'border-[#F5E4A8] border-l-[#D97706] bg-[#FBF3D0]',
      icon: Clock,
      label: 'text-[#B7791F]',
      dot: 'bg-[#D97706]',
      title: 'text-[#1F2937]',
      body: 'text-[#7A6A34]'
    },
    green: {
      wrap: 'border-[#BBE5C8] border-l-[#16A34A] bg-[#E9F8EE]',
      icon: CheckCircle2,
      label: 'text-[#15803D]',
      dot: 'bg-[#16A34A]',
      title: 'text-[#14532D]',
      body: 'text-[#3F6B4F]'
    },
    red: {
      wrap: 'border-[#F5C2C2] border-l-[#DC2626] bg-[#FDECEC]',
      icon: AlertTriangle,
      label: 'text-[#B42318]',
      dot: 'bg-[#DC2626]',
      title: 'text-[#7A271A]',
      body: 'text-[#98524B]'
    },
    slate: {
      wrap: 'border-border border-l-slate-400 bg-muted/50',
      icon: Info,
      label: 'text-muted-foreground',
      dot: 'bg-slate-400',
      title: 'text-foreground',
      body: 'text-muted-foreground'
    }
  }
  const t = tones[tone] ?? tones.slate
  const Icon = t.icon
  return (
    <AdasBox
      role='status'
      className={`flex flex-wrap items-center justify-between gap-4 rounded-xl border border-l-4 px-5 py-4 shadow-sm ${t.wrap}`}
    >
      <AdasBox className='flex min-w-0 items-start gap-3'>
        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${t.label}`} aria-hidden='true' />
        {label && (
          <span
            className={`mt-1 inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] font-bold uppercase tracking-wide ${t.label}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />
            {label}
          </span>
        )}
        <AdasBox className='min-w-0'>
          <AdasTypography className={`text-[14px] font-bold ${t.title}`}>{title}</AdasTypography>
          {body && <AdasTypography className={`text-[13px] ${t.body}`}>{body}</AdasTypography>}
        </AdasBox>
      </AdasBox>
      {action}
    </AdasBox>
  )
}
StatusBanner.propTypes = {
  tone: PropTypes.oneOf(['amber', 'green', 'red', 'slate']),
  label: PropTypes.node,
  title: PropTypes.node.isRequired,
  body: PropTypes.node,
  action: PropTypes.node
}

// Dark navy primary button (Update payment method, Contact us).
export const DarkButton = ({ children, onClick, fullWidth = false, className = '', type = 'button', disabled = false }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex h-10 items-center justify-center rounded-lg bg-[#0A1628] px-4 text-[14px] font-semibold text-white transition-colors hover:bg-[#1E293B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0A1628]/40 disabled:cursor-not-allowed disabled:opacity-60 ${
      fullWidth ? 'w-full' : ''
    } ${className}`}
  >
    {children}
  </button>
)
DarkButton.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.string,
  disabled: PropTypes.bool
}

// White bordered secondary button (Compare plans, Back).
export const OutlineButton = ({ children, onClick, fullWidth = false, className = '', disabled = false }) => (
  <button
    type='button'
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex h-9 items-center justify-center rounded-lg border border-border bg-white px-4 text-[14px] font-semibold text-foreground transition-colors hover:bg-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0A1628]/20 disabled:cursor-not-allowed disabled:opacity-60 ${
      fullWidth ? 'w-full' : ''
    } ${className}`}
  >
    {children}
  </button>
)
OutlineButton.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool
}
